import parse, { domToReact } from "html-react-parser";
import React, { ReactElement } from "react";

import Styles from "./rich-text.module.css";

interface RichTextProps {
  richText: string;
  className?: string;
  bulletIcon?: ReactElement | null;
  dynamicValue?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  id?: string;
  preserveFontSize?: boolean; // When true, preserves inline font-size from CMS
}

const CustomListItem = React.memo(function CustomListItem({
  children,
  bulletIcon,
}: {
  children: React.ReactNode;
  bulletIcon: ReactElement;
}) {
  return (
    <li className="flex flex-1 gap-2">
      <div>{bulletIcon}</div>
      <span>{children}</span>
    </li>
  );
});

export default function RichText({
  richText,
  className = "",
  bulletIcon = null,
  onClick,
  dynamicValue,
  id,
  preserveFontSize = false,
}: Readonly<RichTextProps>) {
  const replaceFn = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (domNode: any) => {
      if (domNode.name === "li" && bulletIcon) {
        return (
          <CustomListItem bulletIcon={bulletIcon}>
            {domToReact(domNode.children)}
          </CustomListItem>
        );
      }

      if (domNode.name === "a" && domNode.attribs?.id === "web-chat") {
        const props = {
          ...domNode.attribs,
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            onClick?.(e);
          },
          role: "button",
          tabIndex: 0,
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
            }
          },
        };
        return React.createElement(
          domNode.name,
          props,
          domToReact(domNode.children)
        );
      }

      if (domNode.name === "a" && domNode.attribs?.id === "remain-days") {
        return (
          <span className="text-text-body text-xs font-bold leading-[27px]">
            {dynamicValue}
          </span>
        );
      }

      return undefined;
    },
    [bulletIcon, dynamicValue, onClick]
  );

  return (
    <div data-testid={id ?? "richText-testid"}>
      {/* <i className="fas fa-check mr-4 mt-2 text-l text-orange"></i> */}
      {richText && (
        <div
          className={`${
            preserveFontSize ? Styles.richTextPreserveSize : Styles.richText
          } ${className} [&_ol]:list-[auto] [&_ol]:pl-spacing-l  [&_ul]:list-disc [&_ul]:pl-spacing-l`}
        >
          {parse(richText, { replace: replaceFn })}
        </div>
      )}
    </div>
  );
}
